import { Component, inject } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IrisService } from '@shared/services/iris.service';
import { customEmailValidator } from '@shared/validators/email-validator';
import {
  canadaZipCodeValidator,
  usZipCodeValidator,
} from '@shared/validators/zip-code-validator';
import { invalidPhoneNumberValidator } from '@shared/validators/phonenumber-validator';
import { DialogSize } from '@wcg/frontend-framework';
import { Subject, takeUntil } from 'rxjs';

export interface DialogData {
  title: string;
  dialogSize: DialogSize;
  buttonTitle: string;
  btnEvent: string;
  inputLable: string;
  inputType: string;
  dropDownList: any;
  rowData: any;
  isDefaultVisible: boolean;
}

@Component({
  selector: 'app-contact-manage',
  templateUrl: './contact-manage.component.html',
  styleUrls: [
    './contact-manage.component.scss',
    '../../../shared/styles/grid.scss',
  ],
  standalone: false,
})
export class ContactManageComponent {
  public readonly dialogRef = inject(MatDialogRef<ContactManageComponent>);
  public readonly data = inject<DialogData>(MAT_DIALOG_DATA);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _irisService = inject(IrisService);

  selectedDefaultvalue: any;
  dataObj: any;
  inputValue = new FormControl('', [Validators.required]);
  mailingAddressForm!: FormGroup;
  countries: any[] = [];
  states: any[] = [];
  private destroy$: Subject<boolean> = new Subject();
  isDefault: boolean = false;
  stateLabel = 'State/Province';
  searchTerm: string = '';
  constructor() {
    this.dataObj = this.data;

    if (this.data.inputType == 'mailing-address-json') {
      this.mailingAddressForm = this._formBuilder.group({
        name: new FormControl('', Validators.required),
        address1: new FormControl('', [
          Validators.required,
          Validators.maxLength(100),
        ]),
        address2: new FormControl(''),
        address3: new FormControl(''),
        city: new FormControl('', [
          Validators.required,
          Validators.maxLength(50),
        ]),
        country_id: new FormControl('', Validators.required),
        state: new FormControl(''),
        zip_code: new FormControl('', [Validators.required]),
      });
      this.mailingAddressForm.reset();
      this._irisService
        .getCountries()
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          this.countries = res.data;
        });
      if (this.data.btnEvent == 'update') {
        this.getStates(this.data.rowData.country?.id);

        const addressFormValues = {
          name: this.data.rowData.name,
          address1: this.data.rowData.address1,
          address2: this.data.rowData.address2,
          address3: this.data.rowData.address3,
          city: this.data.rowData.city,
          country_id: this.data.rowData.country?.id,
          state: this.data.rowData.state,
          zip_code: this.data.rowData.zip_code,
        };

        // Use patchValue or setValue to set the values
        this.mailingAddressForm.patchValue(addressFormValues);
      }
    } else {
      this.inputValue.reset();
      if (this.data.inputType == 'number') {
        this.inputValue.setValidators([
          Validators.required,
          Validators.maxLength(20),
          invalidPhoneNumberValidator(),
        ]);
      } else if (this.data.inputType == 'email') {
        this.inputValue.setValidators([
          Validators.required,
          Validators.maxLength(255),
          customEmailValidator(),
        ]);
      }
      if (this.data.btnEvent == 'update') {
        if (this.data.inputType != 'dropdown') {
          this.inputValue.patchValue(this.data.rowData.value);
        } else {
          this.inputValue.patchValue(this.data.rowData.id);
        }
      }
    }
  }

  isValid() {
    if (this.dataObj.inputType == 'mailing-address-json')
      return this.mailingAddressForm.valid;
    return this.inputValue.valid;
  }

  cancel() {
    this.dialogRef.close({
      event: 'cancel',
    });
  }

  clearInput(control?: string) {
    if (control) {
      this.mailingAddressForm.controls[control].reset();
    } else {
      this.inputValue.reset();
    }
  }
  manageContactInfo(btnEvent: string) {
    this.dialogRef.close({
      event: btnEvent,
      isDefault: this.isDefault,
      value:
        this.dataObj.inputType == 'mailing-address-json'
          ? this.mailingAddressForm.getRawValue()
          : this.inputValue.value,
      countries: this.countries,
      states: this.states,
    });
  }

  countryChanged(countryId: string) {
    if (countryId && countryId !== '0') {
      this.getStates(countryId);
      this.mailingAddressForm.controls['state'].reset();
    }
  }

  getStates(countryId: string) {
    this._irisService
      .getStates(countryId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((states) => {
        this.states = [{ name: '<None>', id: null }, ...states.data];
        const selectedCountryId = countryId;
        const usaId = this.countries.find(
          (country) => country.name === 'United States',
        )?.id;
        const canadaId = this.countries.find(
          (country) => country.name === 'Canada',
        )?.id;

        switch (selectedCountryId) {
          case usaId:
            this.stateLabel = 'State';
            this.mailingAddressForm.controls['state'].setValidators(
              Validators.required,
            );
            this.mailingAddressForm.controls.zip_code.setValidators([
              Validators.required,
              Validators.maxLength(10),
              usZipCodeValidator(),
            ]);
            break;
          case canadaId:
            this.stateLabel = 'Province';
            this.mailingAddressForm.controls['state'].setValidators(
              Validators.required,
            );
            this.mailingAddressForm.controls.zip_code.setValidators([
              Validators.required,
              Validators.maxLength(10),
              canadaZipCodeValidator(),
            ]);
            break;
          default:
            this.stateLabel = 'State/Province';
            this.mailingAddressForm.controls['state'].setValidators(
              Validators.maxLength(54),
            );
            this.mailingAddressForm.controls.zip_code.setValidators([
              Validators.required,
              Validators.maxLength(30),
            ]);
            this.states = [];
        }
      });
  }
}
